import type { CapabilitySnapshot, FeatureKey } from "@/features/ats-sync/types";

export type GateDecision = { allowed: boolean; reason: string | null };

/** Backend-authored description for a feature, if any. */
function featureDescription(
  snap: CapabilitySnapshot,
  key: FeatureKey,
): string | null {
  return snap.features[key]?.description ?? null;
}

/**
 * Whether a stage move on a linked job syncs to the ATS.
 * When stage_sync is unsupported, drags still work locally but carry a
 * "won't sync" marker. workaround-state means it syncs (possibly with caveats).
 */
export function stageChangeSync(
  snap: CapabilitySnapshot,
  ats: string,
): { syncs: boolean; reason: string | null } {
  if (snap.features.stage_sync?.state === "unsupported") {
    return {
      syncs: false,
      reason:
        featureDescription(snap, "stage_sync") ??
        `Stage changes stay in Arbi — ${ats} doesn't track pipeline stages.`,
    };
  }
  return { syncs: true, reason: null };
}

/** Whether a note written in Arbi will be pushed to the ATS. */
export function noteSync(
  snap: CapabilitySnapshot,
  ats: string,
): GateDecision {
  if (snap.features.push_note?.state === "unsupported") {
    return {
      allowed: false,
      reason:
        featureDescription(snap, "push_note") ??
        `Won't sync to ${ats} — notes aren't supported.`,
    };
  }
  return { allowed: true, reason: null };
}

/** Whether the reject-application affordance is available for this connector. */
export function rejectAvailability(
  snap: CapabilitySnapshot,
  ats: string,
  hasReasons: boolean,
): GateDecision {
  if (snap.features.reject_application?.state === "unsupported") {
    return { allowed: false, reason: `${ats} can't receive rejections.` };
  }
  if (!hasReasons) {
    return {
      allowed: false,
      reason: `${ats} exposes no rejection reasons, so Arbi can't reject here.`,
    };
  }
  return { allowed: true, reason: null };
}

/** Hint shown when an application is still in the Sourced stage. */
export function sourcedSyncHintText(ats: string): string {
  return `Move out of Sourced to sync to ${ats}.`;
}
